import { AuthController, AuthProvider, useAuth } from "@flaminem/authentication";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";

import { authConfig } from "src/core/authentication/autConfig";
import { setToken } from "src/core/authentication/bearerToken";
import { SpinnerFullPage } from "src/core/components/custom-ui/spinner-full-page";

type TokenAuthProviderProps = {
  children: ReactNode;
};

// const authController = new AuthController(authConfig);
const useAuthController = () => useMemo(() => new AuthController(authConfig), []);

/**
 * TokenAuthProvider :
 * Rôle fonctionnel :
 * - Initialiser la lib d'auth (@flaminem/authentication).
 * - Garantir que le token est injecté dans le store global (bearerToken)
 *   avant de rendre le reste de l'application.
 *
 * Pourquoi :
 * - Évite que les premières requêtes GraphQL partent sans header Authorization,
 *   causant des 401/403 et des redirections intempestives.
 */
export const TokenAuthProvider: FC<TokenAuthProviderProps> = ({ children }) => {
  const authController = useAuthController();
  return (
    <AuthProvider authController={authController}>
      <TokenInjectionGate>{children}</TokenInjectionGate>
    </AuthProvider>
  );
};

const TokenInjectionGate: FC<{ children: ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const accessToken = auth.user?.accessToken ?? null;

  /**
   * On ne rend pas l'app tant que le token n'est pas injecté dans notre store global (bearerToken).
   * Sinon les 1ères queries GraphQL partent sans Authorization et déclenchent un 403/401 + redirect.
   */
  const [isTokenReady, setIsTokenReady] = useState(false);

  useEffect(() => {
    if (!auth.isAuthenticated || !accessToken) {
      setIsTokenReady(false);
      return;
    }

    setToken(accessToken);
    setIsTokenReady(true);
  }, [auth.isAuthenticated, accessToken]);

  /**
   * Boot auth :
   * On attend que la lib d'auth ait terminé son initialisation.
   */
  if (!auth.user && !auth.isAuthenticated) {
    return <SpinnerFullPage />;
  }

  /**
   * Non authentifié :
   * La redirection/login est gérée par la lib.
   */
  if (!auth.isAuthenticated) {
    return null;
  }

  /**
   * Auth OK mais token pas encore injecté :
   * on attend pour éviter des requêtes réseau non authentifiées.
   */
  if (!isTokenReady) {
    return <SpinnerFullPage />;
  }

  return <>{children}</>;
};
